/**
 * LE CATALOGUE DES TYPES DE CHAMP : ce que chaque `type` de `customFieldTypeSchema`
 * EST, ecrit une seule fois (`docs/socle/champs-types.md`, section 2).
 *
 * LE PROBLEME QU'IL FERME. La liste des types etait recopiee partout ou l'on
 * avait besoin d'un mot pour un type : le journal des declarations (« oui/non »),
 * la comparaison des jeux partages (« case »), la liste du Studio (« oui / non »),
 * les editeurs de l'interface, le serveur MCP. Chaque copie vivait sa vie : un
 * type neuf oublie dans une table s'affichait par sa clef technique, et le meme
 * booleen portait trois noms. Ici, un type neuf s'ajoute en UNE entree, et toutes
 * les tables qui la lisent le connaissent.
 *
 * POURQUOI UN `.mjs` (avec son `.d.mts`), comme `portee-jeu.mjs` et
 * `jeux-partages.mjs` : ce catalogue est lu tel quel par ces modules, par les
 * scripts de la CI et par les Functions, sous Node 22 sans transpilation. Le
 * cote type (`types-de-champ.ts`) le reexporte avec ses helpers de valeur.
 *
 * CE QUE PORTE UNE ENTREE :
 * - `libelle` : le mot du Studio, en minuscules (la liste « Type » d'un champ) ;
 * - `genre` : le mot court d'une phrase (journal, ecart entre deux jeux) ;
 * - `nature` : la FORME de la valeur stockee (`texte`, `nombre`, `booleen`,
 *   `liste`, `date`, `dateheure`, `periode`). Aucun type n'invente une forme :
 *   le sac `customFields` n'accepte que chaine, nombre, booleen, liste de
 *   chaines ou `null` (`base.ts`) ;
 * - `numerique` : le type entre dans une formule (`formule.ts`) ;
 * - `sommable` : une colonne de ce type se totalise (pied de tableau, stats) ;
 * - `automatique` : la valeur est posee par le socle (calcul, numero, lecture), jamais
 *   saisie : l'ecran la montre en lecture, le serveur refuse de l'ecrire ;
 * - `reglages` : les clefs de reglage que ce type accepte, et lui seul. Une
 *   clef de cette liste posee sur un autre type est une faute
 *   (`custom-field.ts`).
 *
 * L'ORDRE DES ENTREES est celui de `customFieldTypeSchema`, et un test le
 * verifie (`tests/types-de-champ.test.mjs`) : les deux listes ne peuvent pas
 * diverger sans rougir.
 */

/** Toutes les clefs de reglage d'un champ, quel que soit son type. */
export const REGLAGES_DE_CHAMP = Object.freeze(['options', 'dependDe', 'reference', 'plusieurs', 'unite', 'devise', 'decimales', 'affichage', 'max', 'formule', 'sequence', 'lecture']);

const fiche = (libelle, genre, nature, drapeaux = {}, reglages = []) => Object.freeze({
  libelle,
  genre,
  nature,
  numerique: drapeaux.numerique === true,
  sommable: drapeaux.sommable === true,
  automatique: drapeaux.automatique === true,
  reglages: Object.freeze(reglages),
});

/*
 * L'EXISTANT SE GARDE, ET GAGNE LES CAPACITES NEUVES (regle du fondateur du
 * 27/10/2026, `docs/socle/champs-types.md`, section 1). Un prix reste un
 * `number` : il devient un montant en portant `devise`. Un pourcentage et une
 * duree restent un `number` habille par sa colonne (`pourcentage`, `duree`), et
 * la jauge est un `affichage` du nombre. Des liens multiples sont un `lien`
 * qui porte `plusieurs`. Aucun type ne double donc un autre : un champ deja
 * declare chez un client ne change ni de nom, ni de type, ni de valeur.
 */
export const CATALOGUE_DES_TYPES = Object.freeze({
  text: fiche('texte', 'texte', 'texte'),
  textarea: fiche('texte long', 'texte long', 'texte'),
  number: fiche('nombre', 'nombre', 'nombre', { numerique: true, sommable: true }, ['unite', 'devise', 'decimales', 'affichage']),
  boolean: fiche('oui / non', 'oui/non', 'booleen', { numerique: true }),
  date: fiche('date', 'date', 'date'),
  datetime: fiche('date et heure', 'date et heure', 'dateheure'),
  select: fiche('liste de choix', 'liste', 'texte', {}, ['options', 'dependDe']),
  multiselect: fiche('choix multiples', 'liste à choix multiples', 'liste', {}, ['options', 'dependDe']),
  email: fiche('email', 'e-mail', 'texte'),
  phone: fiche('téléphone', 'téléphone', 'texte'),
  url: fiche('adresse web', 'adresse web', 'texte'),
  /* La nature d'un `lien` est `texte` (un id) ; avec `plusieurs`, une liste d'ids (`natureDuChamp`). */
  lien: fiche('lien vers une fiche', 'lien', 'texte', {}, ['reference', 'plusieurs']),
  personne: fiche("personne de l'organisation", 'personne', 'texte', {}, ['reference']),
  /* La realisation de la nature « liste de textes » du Studio, qui n'avait pas de type. */
  etiquettes: fiche('étiquettes libres', 'étiquettes', 'liste'),
  periode: fiche('période', 'période', 'periode'),
  checklist: fiche('liste de contrôle', 'liste de contrôle', 'liste'),
  note: fiche('note', 'note', 'nombre', { numerique: true }, ['max']),
  calcul: fiche('calcul', 'calcul', 'nombre', { numerique: true, sommable: true, automatique: true }, ['formule', 'devise', 'unite', 'decimales']),
  numero: fiche('numéro automatique', 'numéro', 'texte', { automatique: true }, ['sequence']),
  /* La nature `texte` est celle par defaut : ce qui s'affiche suit le champ LU (son type, ses options), jamais le type `lecture` (`docs/socle/champ-lie-lecture.md`). */
  lecture: fiche('champ lu sur la fiche liée', 'champ lu', 'texte', { automatique: true }, ['lecture']),
});

/** Les types, dans l'ordre du catalogue (celui de `customFieldTypeSchema`). */
export const IDS_TYPES_DE_CHAMP = Object.freeze(Object.keys(CATALOGUE_DES_TYPES));

/*
 * LA LECTURE PASSE PAR `Object.hasOwn`, jamais par `CATALOGUE[type]` nu : un
 * type lu dans un fichier ou une saisie peut valoir `constructor` ou
 * `toString`, que tout objet porte par son prototype.
 */
/** La fiche d'un type, ou `undefined` pour un type inconnu. */
export const ficheDuType = (type) => (typeof type === 'string' && Object.hasOwn(CATALOGUE_DES_TYPES, type) ? CATALOGUE_DES_TYPES[type] : undefined);
/** Le mot court d'un type dans une phrase ; sa clef quand le type est inconnu. */
export const genreDuType = (type) => ficheDuType(type)?.genre ?? String(type);
/** Le mot du Studio pour un type ; sa clef quand le type est inconnu. */
export const libelleDuType = (type) => ficheDuType(type)?.libelle ?? String(type);
/** La forme de la valeur stockee, ou `undefined` pour un type inconnu. */
export const natureDuType = (type) => ficheDuType(type)?.nature;
/** Vrai quand un champ de ce type peut entrer dans une formule. */
export const estNumerique = (type) => ficheDuType(type)?.numerique === true;
/** Vrai quand une colonne de ce type se totalise. */
export const estSommable = (type) => ficheDuType(type)?.sommable === true;
/** Vrai quand la valeur est posee par le socle et ne se saisit pas. */
export const estAutomatique = (type) => ficheDuType(type)?.automatique === true;
/** Vrai quand ce type accepte cette clef de reglage. */
export const accepteReglage = (type, reglage) => ficheDuType(type)?.reglages.includes(reglage) === true;
/**
 * La forme de la valeur stockee d'un CHAMP : celle de son type, sauf un `lien`
 * qui porte `plusieurs`, dont la valeur est une liste d'ids. `undefined` pour
 * un type inconnu.
 */
export const natureDuChamp = (champ) => (champ?.type === 'lien' && champ.plusieurs === true ? 'liste' : natureDuType(champ?.type));

/*
 * LES TYPES FONDUS SE RELISENT DANS LEUR FORME NOUVELLE (`docs/socle/champs-types.md`,
 * section 1). `montant`, `pourcentage`, `duree` et `liens` ont vecu le temps
 * d'une recette (jamais livres en production) avant de se fondre dans
 * l'existant : une instance enregistree entre-temps les porte encore, et le
 * schema les refuse. Sans cette relecture, l'app retombait sur son modele et
 * les champs de la fiche disparaissaient de l'ecran (donnees en base,
 * invisibles). Meme geste que `sansPorteeInterdite` (`portee-jeu.mjs`) : la
 * lecture recoit la forme nouvelle, la publication suivante l'ecrit.
 *
 * SEULE LA DECLARATION CHANGE, jamais une valeur stockee : un montant, un
 * pourcentage et une duree etaient deja des nombres, des liens deja une liste
 * d'ids, et c'est exactement ce que leur forme nouvelle lit.
 */

/** La devise d'un montant d'avant qui n'en declarait pas (`DEVISE_PAR_DEFAUT`, `types-de-champ.ts`). */
const DEVISE_D_AVANT = 'EUR';

/** Chaque type fondu, et le champ qu'il devient (sa clef, son libelle et le reste gardes). */
const RELECTURE_DES_TYPES_FONDUS = Object.freeze({
  /* Un nombre qui porte une devise est un montant : la sienne, sinon EUR. */
  montant: (champ) => ({ ...champ, type: 'number', devise: typeof champ.devise === 'string' && champ.devise.trim() !== '' ? champ.devise : DEVISE_D_AVANT }),
  /* Le pourcentage d'avant etait la jauge (de 0 a 100). */
  pourcentage: (champ) => ({ ...champ, type: 'number', affichage: 'jauge' }),
  /* Des MINUTES. L'habillage `duree` (« 2 h 30 ») vit sur la colonne de
     l'ecran, pas sur le champ : la relecture ne touche que le fichier de
     champs, donc l'unite dit au moins ce que le nombre compte. */
  duree: (champ) => ({ ...champ, type: 'number', unite: 'min' }),
  /* Des liens multiples, la cible (`reference`) gardee. */
  liens: (champ) => ({ ...champ, type: 'lien', plusieurs: true }),
});

/** Les types d'avant la fusion du 27/10/2026, que la lecture recoit encore. */
export const TYPES_FONDUS = Object.freeze(Object.keys(RELECTURE_DES_TYPES_FONDUS));

const objet = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const estFondu = (champ) => objet(champ) && typeof champ.type === 'string' && Object.hasOwn(RELECTURE_DES_TYPES_FONDUS, champ.type);

/** Une declaration de champs sans type fondu (chacun relu dans sa forme nouvelle) ; la meme si rien n'est a relire. */
export function sansTypeFondu(champs) {
  if (!objet(champs) || !Array.isArray(champs.jeux) || !champs.jeux.some((j) => objet(j) && Array.isArray(j.champs) && j.champs.some(estFondu))) return champs;
  return { ...champs, jeux: champs.jeux.map((j) => {
    if (!objet(j) || !Array.isArray(j.champs) || !j.champs.some(estFondu)) return j;
    return { ...j, champs: j.champs.map((champ) => (estFondu(champ) ? RELECTURE_DES_TYPES_FONDUS[champ.type](champ) : champ)) };
  }) };
}
